/**
 * E3 -- can this viewer actually do this station's work?
 *
 * The Journey offered a "Go" on every step, and several of them land
 * on surfaces that refuse the viewer -- the walkthrough's "Go links
 * land on walls". A journey that sends you into a wall is worse than
 * one that tells you who the step is waiting on, because you find out
 * by being turned away.
 *
 * Mirrors the gates the servers apply. Deliberately conservative: when
 * a persona has no clean mapping, the viewer is NOT offered the
 * action, because being told to wait is a smaller cost than being sent
 * into a refusal.
 */
import type { Owner } from "shared/journey/stepMeta";

export interface Viewer {
  is_admin?: boolean;
  positions?: string[];
  realm_roles?: string[];
}

export function canActOn(owner: Owner, me: Viewer | undefined): boolean {
  if (!me) return false;
  if (me.is_admin) return true;
  const positions = me.positions ?? [];
  const roles = me.realm_roles ?? [];
  switch (owner) {
    // Bank setup, connectors and the foundation registers are all
    // admin-gated; is_admin above is the only way in.
    case "admin":
    case "steward":
      return false;
    case "opsres":
      return roles.includes("head_of_opsres");
    case "cro":
      return roles.includes("cro");
    // H -- a business owner holds it too: the estate records ownership
    // under both realm roles, and the tolerance form admits either.
    case "service_owner":
      return roles.includes("service_owner") || roles.includes("business_owner");
    case "reviewer":
      return positions.includes("reviewer");
    case "approver":
      return positions.includes("approver");
    // The framework module's own three-position workflow.
    case "compliance":
      return (
        positions.includes("preparer") ||
        positions.includes("reviewer") ||
        positions.includes("approver")
      );
    // Reading the live twin needs no position.
    case "ops":
      return true;
    default:
      return false;
  }
}

/**
 * E2 -- is this station MINE, as opposed to one I could perform?
 *
 * "Only my steps" was a no-op for an administrator, because
 * `canActOn` admits admin everywhere: they hold every gate, so every
 * step came back as theirs and the filter removed nothing. Those are
 * two different questions. May I do this is about authority; is this
 * mine is about whose job it is, and a filter that cannot tell them
 * apart is a filter that does nothing for the person most likely to
 * use it.
 *
 * The same MINE / QUEUE distinction the inbox draws, one surface over.
 */
export function isMyStation(owner: Owner, me: Viewer | undefined): boolean {
  if (!me) return false;
  const positions = me.positions ?? [];
  const roles = me.realm_roles ?? [];
  switch (owner) {
    // An administrator's OWN work: standing the platform up and
    // keeping the registers straight.
    case "admin":
    case "steward":
      return me.is_admin ?? false;
    case "opsres":
      return roles.includes("head_of_opsres");
    case "cro":
      return roles.includes("cro");
    case "service_owner":
      return roles.includes("service_owner") || roles.includes("business_owner");
    case "reviewer":
      // The Resilience Lead records the second-line challenge when
      // she is standing in for the reviewer (D8), so the station is
      // on her list too -- listing it for nobody is what left her
      // unable to find the work she is expected to chase.
      return positions.includes("reviewer") || roles.includes("head_of_opsres");
    case "approver":
      return positions.includes("approver");
    case "compliance":
      return (
        positions.includes("preparer") ||
        positions.includes("reviewer") ||
        positions.includes("approver")
      );
    // Reading the live twin is nobody's station in particular.
    case "ops":
      return false;
    default:
      return false;
  }
}
